import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/i18n";
import { CursorProvider } from "@/context/CursorContext";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Portfolio from "@/pages/Portfolio";
import AboutPage from "@/pages/AboutPage";
import WorkIndexPage from "@/pages/WorkIndexPage";
import ShowcasePage from "@/pages/ShowcasePage";
import PrivateWorkPage from "@/pages/PrivateWorkPage";
import CaseStudyPage from "@/pages/CaseStudyPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { completeGoogle } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const params = new URLSearchParams((location.hash || "").replace(/^#/, ""));
    const sessionId = params.get("session_id");
    (async () => {
      try {
        if (sessionId) await completeGoogle(sessionId);
      } catch {
        /* handled by redirect below */
      } finally {
        window.history.replaceState(null, "", "/admin");
        navigate("/admin", { replace: true });
      }
    })();
  }, [location.hash, completeGoogle, navigate]);

  return (
    <div className="min-h-screen bg-void flex items-center justify-center">
      <span className="font-mono text-xs tracking-[0.3em] uppercase text-bone/50">Signing you in…</span>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { admin } = useAuth();
  if (admin === undefined) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <span className="font-mono text-xs tracking-[0.3em] uppercase text-bone/40">Loading…</span>
      </div>
    );
  }
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
}

function RouterInner() {
  const location = useLocation();
  // Handle Google OAuth callback (session_id in URL fragment) before anything else
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Portfolio />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/work" element={<WorkIndexPage />} />
      <Route path="/work/private" element={<PrivateWorkPage />} />
      <Route path="/work/:slug" element={<ShowcasePage />} />
      <Route path="/case/:slug" element={<CaseStudyPage />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <LanguageProvider>
          <CursorProvider>
            <div className="App">
              <Toaster
                position="bottom-right"
                toastOptions={{
                  style: {
                    background: "#141414",
                    border: "1px solid rgba(252,249,242,0.14)",
                    color: "#fcf9f2",
                    fontFamily: "'roc-grotesk', sans-serif",
                    fontSize: "12px",
                    letterSpacing: "0.05em",
                    borderRadius: "2px",
                  },
                }}
              />
              <BrowserRouter>
                <RouterInner />
              </BrowserRouter>
            </div>
          </CursorProvider>
        </LanguageProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
